<script setup>
import { ref, computed, onMounted, getCurrentInstance } from "vue";
import { useRoute } from "vue-router";
import moment from "moment";
import api from "../api/api.js";
import { flashMessage } from "../functions.js";

// NineStar Connect (utility / internet) — C01 driven by CallerInfo.
// NineStar is not a bank: CallerInfo.MemberNumber is presented as "Customer Number".

const route   = useRoute();
const company = localStorage.getItem("companyID");
const authToken = ref("");

const { proxy } = getCurrentInstance();
const logocheck = require("@/assets/check.png");
const logoclose = require("@/assets/close.png");

function flash(message, color = "#0e5071") {
  flashMessage(proxy.$flashMessage, color, "#ffffff", message);
}

// ── Caller info ──────────────────────────────────────────────────────────────
const callerInfo = ref(null);
const isLoading  = ref(true);

const parseJson = (raw, fallback) => {
  if (!raw) return fallback;
  if (typeof raw === "object") return raw;
  try { return JSON.parse(raw); } catch { return fallback; }
};

const accounts = computed(() => {
  const list = parseJson(callerInfo.value?.Accounts, []);
  return Array.isArray(list) ? list : [];
});

const activeAccount = computed(() => {
  const ci = callerInfo.value;
  if (!ci || !accounts.value.length) return {};
  return accounts.value.find(a => String(a.Name) === String(ci.MemberName)) || accounts.value[0];
});

const phoneNumbers = computed(() => {
  const p = parseJson(callerInfo.value?.PhoneNumbers, {});
  return { cellPhones: p.cellPhones || [], other: p.other || [] };
});

const customerNumber = computed(() => (callerInfo.value?.MemberNumber || "").trim());

// Mobile numbers first (shown green), then the rest
const enrolledNumbers = computed(() => [
  ...phoneNumbers.value.cellPhones.map(number => ({ number, type: "mobile" })),
  ...phoneNumbers.value.other.map(number => ({ number, type: "other" })),
]);
const customerName = computed(() => callerInfo.value?.MemberName || activeAccount.value.Name || "");
const customerEmail   = computed(() => activeAccount.value.Email || "");
const customerAddress = computed(() => activeAccount.value.Address || "");
const callerId = computed(() => (callerInfo.value?.CallerID || "").replace(/^tel:/, ""));

const customerSince = computed(() => {
  const d = callerInfo.value?.MemberSince;
  if (!d) return "";
  return new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric", timeZone: "UTC" });
});

// Chats have no phone number — show the demo VPN's IP instead
const CHAT_SOURCE_IP = "44.221.221.55";
const isPhoneCaller = computed(() => /\d{7,}/.test(callerId.value));

// Demo: every caller / chat is shown with the US flag
const US_FLAG_URL = "https://flagcdn.com/w40/us.png";

// SMS can't be told from voice by caller ID (both are phone numbers), so the Genesys screen-pop
// URL may pass the channel explicitly: ...&channel=sms | chat | email | voice
const CHANNEL_PARAM = {
  sms: "SMS", message: "SMS", messaging: "SMS",
  chat: "Chat", webchat: "Chat",
  email: "Email",
  voice: "Voice", call: "Voice",
};
const interactionSource = computed(() => {
  const param = String(route.query.channel || route.query.mediaType || "").toLowerCase();
  if (CHANNEL_PARAM[param]) return CHANNEL_PARAM[param];
  const id = callerId.value;
  if (!id) return "";
  if (id.includes("@")) return "Email";
  if (id.toLowerCase() === "chat") return "Chat";
  if (id.toLowerCase() === "sms") return "SMS";
  if (id === "itmvideo") return "Video";
  return "Voice";
});

// Same status codes and labels as M01
const AUTH_STATUS = {
  "0": { label: "Not Authenticated",       color: "#b91c1c" },
  "1": { label: "Authenticated",           color: "#15803d" },
  "2": { label: "Not Authenticated",       color: "#b91c1c" },
  "3": { label: "Manually Authenticated",  color: "#15803d" },
  "4": { label: "Manually Failed",         color: "#b91c1c" },
  "5": { label: "Illuma Authenticated",    color: "#15803d" },
  "6": { label: "Illuma Unauthenticated",  color: "#b91c1c" },
};
const authStatusNum = computed(() => String(callerInfo.value?.AuthStatus ?? "0"));
const authStatus = computed(() => AUTH_STATUS[authStatusNum.value] || AUTH_STATUS["0"]);
const isVerified = computed(() => ["1", "3", "5"].includes(authStatusNum.value));

// ── Manual authentication — same calls as M01's handleAuth, written to CallerInfo ──
const authSaving   = ref(false);
const authRevertTo = ref(null); // previous status, so Cancel can undo (M01 behaviour)

async function applyAuthStatus(status) {
  const ci = callerInfo.value;
  if (!ci || !authToken.value) return false;
  const failureReason = status === "4" ? "Manually Failed by Agent" : null;
  const attemptType = "2"; // manual
  try {
    const authCall = await api.post("insertAuthCalls", {
      MemberNumber:     ci.MemberNumber,
      MemberName:       ci.MemberName,
      CallerID:         ci.CallerID,
      AuthMethod:       ["3", "4"].includes(status) ? "2" : "1",
      SuccessOrFail:    status,
      Company:          company,
      InteractionId:    ci.InteractionId,
      FailureReason:    failureReason,
      CreditCardNumber: ci.CreditCardNumber,
      AgentID:          ci.AgentId,
      AgentName:        ci.AgentName,
      SourceIp:         ci.SourceIp,
    });
    if (authCall.data?.affectedRows !== 1) return false;

    const callUpdate = await api.post("updateCallsAuthStatus", {
      MemberNumber:  ci.MemberNumber,
      MemberName:    ci.MemberName,
      AuthToken:     authToken.value,
      AuthStatus:    status,
      FailureReason: failureReason,
      AgentID:       ci.AgentId,
      AgentName:     ci.AgentName,
    });
    if (callUpdate.data?.affectedRows !== 1) return false;

    const infoUpdate = await api.post("updateCallerInfoSetAuthStatus", {
      AuthStatus:  status,
      AttemptType: attemptType,
      IndexNum:    authToken.value,
    });
    if (infoUpdate.data?.affectedRows !== 1) return false;

    await loadCallerInfo({ silent: true });
    return true;
  } catch (e) {
    console.error("Auth status update error", e);
    return false;
  }
}

async function setManualAuth(status) {
  if (authSaving.value || status === authStatusNum.value) return;
  const previous = parseInt(authStatusNum.value) || null;
  authSaving.value = true;
  const ok = await applyAuthStatus(status);
  authSaving.value = false;
  if (!ok) {
    flash("Unable to update authentication status.", "#b91c1c");
    return;
  }
  authRevertTo.value = previous;
  flash("Authentication status successfully updated.", status === "3" ? "#15803d" : "#b91c1c");
  await Promise.all([loadCrmRecords(), generateCrmSummary()]);
}

const statusMenuOpen = ref(false);

function chooseAuth(option) {
  statusMenuOpen.value = false;
  if (option === "revert") cancelManualAuth();
  else setManualAuth(option);
}

async function cancelManualAuth() {
  if (authRevertTo.value === null || authSaving.value) return;
  authSaving.value = true;
  const ok = await applyAuthStatus(String(authRevertTo.value));
  authSaving.value = false;
  if (ok) {
    authRevertTo.value = null;
    flash("Authentication status reverted.");
  } else {
    flash("Unable to revert authentication status.", "#b91c1c");
  }
}

// IVR / bot path (IntentHistory) — last step is the current intent
const intentPath = computed(() => {
  const p = parseJson(callerInfo.value?.IntentHistory, []);
  return Array.isArray(p) ? p : [];
});
const currentIntent = computed(() => {
  const last = intentPath.value[intentPath.value.length - 1];
  if (!last) return "Internet Support";
  return typeof last === "string" ? last : (last.intent || last.name || last.label || "Internet Support");
});

function formatPhone(str) {
  const cleaned = String(str || "").replace(/\D/g, "");
  const m = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
  if (!m) return str || "";
  return `${m[1] ? "+1 " : ""}(${m[2]}) ${m[3]}-${m[4]}`;
}

async function loadCallerInfo({ silent = false } = {}) {
  if (!silent) isLoading.value = true;
  try {
    const res = await api.get(`GetCallerInfo/${authToken.value}/${company}`);
    callerInfo.value = res.data || null;
    // Other screens (Notes, History) read these from localStorage, same as M01 sets them
    localStorage.setItem("authToken", authToken.value);
    if (customerNumber.value) localStorage.setItem("MemberNumber", customerNumber.value);
    else localStorage.removeItem("MemberNumber");
    const firstCell = phoneNumbers.value.cellPhones[0];
    otpPhone.value = firstCell || (/\d{7,}/.test(callerId.value) ? callerId.value : "");
  } catch (e) {
    console.error("NineStar caller info error", e);
  } finally {
    isLoading.value = false;
  }
}

async function copyCustomerNumber() {
  try {
    await navigator.clipboard.writeText(customerNumber.value);
    flash("Customer number copied.");
  } catch {
    flash("Unable to copy.", "#b91c1c");
  }
}

// ── Customer search (same lookup as M01; the backend writes the match to CallerInfo) ──
const searchInput = ref("");
const searching   = ref(false);

async function lookupCustomerNumber(number) {
  const res = await api.get(
    `/get-account-by-number?company=${company}&authToken=${authToken.value}&accountNumber=${encodeURIComponent(number)}`
  );
  await loadCallerInfo({ silent: true });
  authRevertTo.value = null;
  crmRecords.value = [];
  sessionNotes.value = [];
  verificationAttempted.value = false;
  otpInput.value = "";
  secCategory.value = "";
  secNote.value = "";
  if (res.data?.Response == true) {
    flash("Customer found.");
    await Promise.all([loadCrmRecords(), generateCrmSummary(), loadCustomerRisk()]);
  } else {
    flash("Customer not found.", "#b91c1c");
    await loadCustomerRisk(); // clears the previous customer's warnings and notes
  }
}

async function searchCustomer() {
  const value = searchInput.value.trim();
  if (!value || !authToken.value) return;
  searching.value = true;
  try {
    await lookupCustomerNumber(value);
    searchInput.value = "";
  } catch (e) {
    console.error("Customer search error", e);
    flash("Search failed.", "#b91c1c");
  } finally {
    searching.value = false;
  }
}

function cancelSearch() {
  searchInput.value = "";
}

// ── CRM (interaction history, AI summary, call notes) ────────────────────────
const crmRecords  = ref([]);
const crmLoading  = ref(false);
const crmChannelFilter      = ref("");
const crmModalChannelFilter = ref("");
const crmModalOpen = ref(false);
const crmSelected  = ref(null);
const crmNote      = ref("");
const crmNoteSaving = ref(false);
const sessionNotes = ref([]);

function filterByChannel(records, filter) {
  if (!filter) return records;
  return records.filter(rec => {
    const ch = (rec.channel || "").toLowerCase();
    if (filter === "voice") return !ch.includes("chat") && !ch.includes("sms") && !ch.includes("email");
    if (filter === "sms")   return ch.includes("sms");
    if (filter === "chat")  return ch.includes("chat") || ch.includes("email");
    return true;
  });
}
const crmFilteredRecords      = computed(() => filterByChannel(crmRecords.value, crmChannelFilter.value));
const crmModalFilteredRecords = computed(() => filterByChannel(crmRecords.value, crmModalChannelFilter.value));

const crmSummary = computed(() => parseJson(callerInfo.value?.CRMSummary, null));

const crmSelectedNotes = computed(() => {
  const raw = crmSelected.value?.notes;
  if (!raw) return "No notes on file for this interaction.";
  const parsed = parseJson(raw, null);
  if (Array.isArray(parsed)) return parsed.join("\n") || "No notes on file for this interaction.";
  return raw;
});

function crmTimeAgo(datetime) {
  return datetime ? moment(datetime).fromNow() : "";
}

function channelIcon(channel) {
  const ch = (channel || "").toLowerCase();
  if (ch.includes("chat"))  return "fa-comments";
  if (ch.includes("sms"))   return "fa-comment-sms";
  if (ch.includes("email")) return "fa-envelope";
  if (ch.includes("video")) return "fa-video";
  return "fa-phone";
}

async function loadCrmRecords() {
  const customerKey = customerNumber.value;
  const ani = callerId.value;
  if (!customerKey && !ani) return;
  crmLoading.value = true;
  try {
    const params = customerKey
      ? `company=${company}&memberNumber=${encodeURIComponent(customerKey)}`
      : `company=${company}&callerId=${encodeURIComponent(ani)}`;
    const res = await api.get(`/get-crm-activity?${params}`);
    crmRecords.value = res.data?.Activities || [];
  } catch (e) {
    console.error("CRM activity fetch error", e);
  } finally {
    crmLoading.value = false;
  }
}

async function generateCrmSummary() {
  if (!isVerified.value) return;
  try {
    const res = await api.post("/v2/CreateCrmActivitySummary", { Company: company, AuthToken: authToken.value });
    if (res.data?.Response && callerInfo.value) {
      callerInfo.value.CRMSummary = JSON.stringify({ summary: res.data.Summary, themes: res.data.Themes });
    }
  } catch {
    // non-critical
  }
}

async function saveCrmNote() {
  const note = crmNote.value.trim();
  if (!note) return;
  crmNoteSaving.value = true;
  try {
    await api.put("/CRMNotes", { company, indexNum: authToken.value, note });
    sessionNotes.value.unshift({ note, at: moment().format("h:mm A") });
    crmNote.value = "";
    flash("Note saved.");
  } catch (e) {
    console.error("CRM note save error", e);
    flash("Unable to save note.", "#b91c1c");
  } finally {
    crmNoteSaving.value = false;
  }
}

function openCrmActivity(record) {
  crmSelected.value = record;
  crmModalOpen.value = true;
}
function closeCrmActivity() {
  crmModalOpen.value = false;
  crmSelected.value = null;
}
function openNISC() {
  window.open("https://demo38web.core-access.com/nisc-outage-mockup.html", "nisc", "width=900,height=820");
}
function openCRM() {
  window.open("https://demo38web.core-access.com/ninestar-crm-mockup.html", "_blank", "width=auto,height=auto");
}

// ── Start Callback (chat/SMS/email → voice callback to the same agent, same as M01) ──
const isChatOrEmail = computed(() => ["Chat", "SMS", "Email"].includes(interactionSource.value));
const isCallbackLoading = ref(false);
const callbackStatus = ref(null);
const callbackModal = ref({ show: false, phone: "" });

// Authenticated caller: call back the number selected for 2FA. Otherwise ask the agent for one.
function openCallback() {
  callbackStatus.value = null;
  if (["1", "3"].includes(authStatusNum.value) && otpPhone.value) {
    startCallback(otpPhone.value);
  } else {
    callbackModal.value = { show: true, phone: "" };
  }
}

async function submitCallbackModal() {
  const phone = callbackModal.value.phone.trim();
  if (!phone) return;
  callbackModal.value.show = false;
  await startCallback(phone);
}

async function startCallback(phone) {
  isCallbackLoading.value = true;
  callbackStatus.value = null;
  try {
    const res = await api.post("/v2/CreateCallback", {
      Company:     company,
      AuthToken:   authToken.value,
      PhoneNumber: phone,
    });
    callbackStatus.value = res.data?.Response ? "success" : (res.data?.Error || "Failed");
  } catch (e) {
    callbackStatus.value = e.response?.data?.Error || "Failed to create callback";
  } finally {
    isCallbackLoading.value = false;
    if (callbackStatus.value === "success") {
      setTimeout(() => { callbackStatus.value = null; }, 4000);
    }
  }
}

// ── 2FA ──────────────────────────────────────────────────────────────────────
const OTP_TOKEN = "2y10D08eZgJLx0C9rGvQKfwFBuCPvqDdmdowpnczUKwYWU3B0VoVt5BiC";
const otpPhone = ref("");
const otpInput = ref("");
const verificationAttempted = ref(false);
const isValid = ref(false);

const otpPhoneOptions = computed(() => {
  const list = [...phoneNumbers.value.cellPhones];
  if (/\d{7,}/.test(callerId.value) && !list.some(p => p.replace(/\D/g, "").endsWith(callerId.value.replace(/\D/g, "").slice(-10)))) {
    list.push(callerId.value);
  }
  return list;
});

async function sendSMS() {
  if (!otpPhone.value) return;
  try {
    const res = await api.post("/v2/SendOTP", {
      Company:      company,
      cellPhone:    otpPhone.value.replace(/[^+\d]/g, ""),
      AuthToken:    authToken.value,
      memberNumber: customerNumber.value,
    }, { headers: { token: OTP_TOKEN } });
    flash(res.data.Response || "OTP sent");
  } catch {
    flash("Failed to send OTP", "#b91c1c");
  }
}

async function verifyOTP() {
  try {
    const res = await api.post("/v2/CheckOTP", {
      Company:   company,
      cellPhone: otpPhone.value.replace(/[^+\d]/g, ""),
      OTP:       otpInput.value,
      AuthToken: authToken.value,
    }, { headers: { token: OTP_TOKEN } });
    verificationAttempted.value = true;
    isValid.value = res.data.Response === "OTP Validated";
    flash(res.data.Response, isValid.value ? "#15803d" : "#b91c1c");
  } catch {
    flash("Failed to verify OTP", "#b91c1c");
  }
}

// ── Internet service (demo data — the contact-center scenario is internet support) ──
const internet = ref({
  plan:        "NineStar Fiber 1 Gig",
  speed:       "1,000 / 1,000 Mbps",
  status:      "Active",
  bundle:      "Internet + Electric",
  installed:   "2023-03-14",
  monthly:     79.95,
});

// Demo scenario: same outage details the Genesys bot flow gives the customer
const outage = ref({
  active:   true,
  area:     "Vernon Township",
  service:  "Internet",
  message:  "Engineering work is ongoing. Estimated time for resolution is by 4PM today. Thank you for your patience.",
  cause:    "Engineering work",
  started:  "Today, 8:15 AM",
  etr:      "4PM today",
  affected: "Approx. 1,200 customers",
});

const billing = ref({ balance: 27.00, due: "2026-10-25", autopay: false, lastPayment: "2026-09-25 — $79.95" });

const fmt = (v) => "$" + Number(v).toLocaleString("en-US", { minimumFractionDigits: 2 });

const statusBadge = (status) => (status === "Active" ? "ns-badge-active" : "ns-badge-muted");

// ── Fraud warnings (same checks as M01's Threshold Warning box) ──────────────
const callerIdFlags = ref(null);
const callerIdUsage = ref([]);
const thresholdWarnings = ref([]);
const hasFraudWarnings = computed(() =>
  (callerIdFlags.value?.failedAuthThisMember > 0) || callerIdUsage.value.length > 0 || thresholdWarnings.value.length > 0
);

async function loadCallerIdFlags() {
  callerIdFlags.value = null;
  if (!isPhoneCaller.value || !customerNumber.value) return;
  try {
    const res = await api.get(`/get-caller-id-flags?company=${company}&callerID=${encodeURIComponent(callerId.value)}&memberNumber=${encodeURIComponent(customerNumber.value)}`);
    callerIdFlags.value = res.data || null;
  } catch (e) {
    console.error("Caller ID flags fetch error", e);
  }
}

async function loadCallerIdUsage() {
  callerIdUsage.value = [];
  if (!isPhoneCaller.value) return;
  try {
    const res = await api.get(`/caller-id-usage?company=${company}&callerID=${encodeURIComponent(callerId.value)}`);
    callerIdUsage.value = Array.isArray(res.data) ? res.data : [];
  } catch (e) {
    console.error("Caller ID usage fetch error", e);
  }
}

// Threshold settings come from T01; each exceeded threshold adds its configured warning message.
// (M01 also emails the configured recipients; this screen only displays the warnings.)
async function loadThresholdWarnings() {
  thresholdWarnings.value = [];
  if (!authToken.value) return;
  let settings;
  try {
    const res = await api.get(`update_threshold/${company}`);
    settings = res.data?.[0];
  } catch {
    return;
  }
  if (!settings) return;
  const warnings = [];

  // Same caller ID calling too often
  if (isPhoneCaller.value && settings.ThresholdActiveCaller !== 0) {
    try {
      const res = await api.post("get-threshold-counter", {
        Company: company, CallerID: callerId.value, NumHoursCaller: settings.NumHoursCaller,
      });
      if (res.data?.[0]?.countIndex >= settings.MaxNumCallsCaller) {
        warnings.push(String(settings.WarningMessageCaller || "")
          .replace(/%callerid_qty/g, settings.MaxNumCallsCaller)
          .replace(/%callerid_numhours/g, settings.NumHoursCaller));
      }
    } catch { /* skip */ }
  }

  // Same customer contacted too often
  if (customerNumber.value && interactionSource.value !== "Email" && settings.ThresholdActiveMember !== 0) {
    try {
      const q = new URLSearchParams({ company, memberNumber: customerNumber.value, numHoursMember: settings.NumHoursMember });
      const res = await api.get(`/get-threshold-counter-member-number?${q}`);
      if (res.data?.[0]?.countIndex >= settings.MaxNumCallsMember) {
        warnings.push(String(settings.WarningMessageMember || "")
          .replace(/%member_qty/g, settings.MaxNumCallsMember)
          .replace(/%member_numhours/g, settings.NumHoursMember));
      }
    } catch { /* skip */ }
  }

  // Same IP address contacting too often
  if (customerNumber.value && settings.ThresholdActiveIPAddress !== 0) {
    try {
      const q = new URLSearchParams({ company, memberNumber: customerNumber.value, numHoursIPAddress: settings.NumHoursIPAddress });
      const res = await api.get(`/get-threshold-counter-ip-address?${q}`);
      if (res.data?.[0]?.countIndex >= settings.MaxNumCallsIPAddress) {
        warnings.push(String(settings.WarningMessageIPAddress || "")
          .replace(/%ip_qty/g, settings.MaxNumCallsIPAddress)
          .replace(/%ip_numhours/g, settings.NumHoursIPAddress));
      }
    } catch { /* skip */ }
  }

  thresholdWarnings.value = warnings.filter(w => w.trim());
}

// ── Security notes (same as M01) ─────────────────────────────────────────────
const noteCategories = ref([]);
const fraudNotes = ref([]);
const secCategory = ref("");
const secNote = ref("");
const secSaving = ref(false);

async function loadNoteCategories() {
  try {
    const { data } = await api.post("getNoteCategory", { Company: company });
    noteCategories.value = Array.isArray(data) ? data.map(c => c.NoteCategory) : [];
  } catch (e) {
    console.error("Note categories fetch error", e);
  }
}

async function loadFraudNotes() {
  fraudNotes.value = [];
  if (!customerNumber.value) return;
  try {
    const { data } = await api.get(`getFraudNotes?company=${company}&memberNumber=${encodeURIComponent(customerNumber.value)}`);
    fraudNotes.value = Array.isArray(data) ? data : [];
  } catch (e) {
    console.error("Security notes fetch error", e);
  }
}

async function saveSecurityNote() {
  const ci = callerInfo.value;
  if (!ci || !customerNumber.value || !secCategory.value || !secNote.value.trim()) return;
  secSaving.value = true;
  try {
    const res = await api.post("addFraudNotes", {
      Company:        company,
      Name:           ci.MemberName,
      MemberNumber:   ci.MemberNumber,
      PhoneNumber:    ci.CallerID,
      InteractionId:  ci.InteractionId,
      NoteCategory:   secCategory.value,
      NoteText:       secNote.value.trim(),
      CreatedByAgent: ci.AgentName,
      AgentID:        ci.AgentId,
      AgentName:      ci.AgentName,
    });
    if (res.status === 200) {
      secCategory.value = "";
      secNote.value = "";
      flash("Security note successfully added.");
      await loadFraudNotes();
    } else {
      flash("Security note not added.", "#b91c1c");
    }
  } catch (e) {
    console.error("Security note save error", e);
    flash("Security note not added.", "#b91c1c");
  } finally {
    secSaving.value = false;
  }
}

// Everything on the left column that depends on who the customer is
function loadCustomerRisk() {
  return Promise.all([loadCallerIdFlags(), loadCallerIdUsage(), loadThresholdWarnings(), loadFraudNotes()]);
}

// ── Init ─────────────────────────────────────────────────────────────────────
onMounted(async () => {
  authToken.value = route.query.AuthToken || localStorage.getItem("authToken") || "";
  if (!authToken.value) { isLoading.value = false; return; }
  await loadCallerInfo();
  await Promise.all([loadCrmRecords(), generateCrmSummary(), loadCustomerRisk(), loadNoteCategories()]);
});
</script>

<template>
  <div class="ns-page">
    <div class="ns-card">

      <!-- ── Card header ──────────────────────────────────────────────────── -->
      <div class="ns-topbar">
        <h2 class="ns-screen-id">Customer Information</h2>
        <div class="ns-topbar-right">
          <button class="ns-goto-btn" @click="openNISC">Go to NISC</button>
          <button class="ns-goto-btn" @click="openCRM">Go to CRM</button>
          <span class="ns-screen-code">C01</span>
        </div>
      </div>

      <div v-if="isLoading" class="ns-loading">Loading customer…</div>
      <div v-else-if="!callerInfo" class="ns-loading">No caller information found for this interaction.</div>

      <template v-else>
        <!-- ── Subheader strip ─────────────────────────────────────────────── -->
        <div class="ns-subheader">
          <div class="ns-sub-main">
            <div class="ns-sub-row">
              <span class="ns-sub-lbl">Customer Number:</span>
              <span class="ns-sub-val ns-sub-val--number">{{ customerNumber }}</span>
              <template v-if="customerNumber">
                <img v-if="customerName" :src="logocheck" alt="valid" title="Valid Customer Number" class="ns-status-icon" />
                <img v-else :src="logoclose" alt="invalid" title="Customer Number not found" class="ns-status-icon" />
                <button class="ns-copy-btn" title="Copy Customer Number" @click="copyCustomerNumber">
                  <i class="fa-regular fa-copy"></i>
                </button>
              </template>
              <!-- Turn this chat/SMS/email into a voice callback to the same agent (same as M01) -->
              <template v-if="isChatOrEmail">
                <button class="ns-callback-btn" :disabled="isCallbackLoading" @click="openCallback">
                  <i class="fa-solid fa-phone"></i> {{ isCallbackLoading ? "Starting…" : "Start Callback" }}
                </button>
                <span v-if="callbackStatus" class="ns-callback-status"
                  :class="callbackStatus === 'success' ? 'is-ok' : 'is-err'">
                  {{ callbackStatus === 'success' ? 'Callback created' : callbackStatus }}
                </span>
              </template>
            </div>
            <div class="ns-sub-row">
              <span class="ns-sub-lbl">Name:</span>
              <span class="ns-sub-val ns-sub-val--name">{{ customerName }}</span>
            </div>
            <div class="ns-sub-row" v-if="customerEmail">
              <span class="ns-sub-lbl">Email:</span>
              <span class="ns-sub-val">{{ customerEmail }}</span>
            </div>
            <div class="ns-sub-row" v-if="isPhoneCaller">
              <span class="ns-sub-lbl">Phone:</span>
              <img :src="US_FLAG_URL" alt="US" class="ns-flag" />
              <span class="ns-sub-val">{{ formatPhone(callerId) }}</span>
            </div>
            <div class="ns-sub-row" v-else-if="interactionSource === 'Chat'">
              <span class="ns-sub-lbl">Source IP:</span>
              <img :src="US_FLAG_URL" alt="US" class="ns-flag" />
              <span class="ns-sub-val">{{ CHAT_SOURCE_IP }}</span>
            </div>
            <div class="ns-sub-row" v-if="customerAddress">
              <span class="ns-sub-lbl">Service Address:</span>
              <span class="ns-sub-val">{{ customerAddress }}</span>
            </div>
            <div class="ns-sub-row" v-if="customerSince">
              <span class="ns-sub-lbl">Customer Since:</span>
              <span class="ns-sub-val">{{ customerSince }}</span>
            </div>
            <div class="ns-sub-row ns-sub-row--top" v-if="enrolledNumbers.length">
              <span class="ns-sub-lbl">Enrolled Numbers:</span>
              <span class="ns-sub-val">
                <template v-for="(n, i) in enrolledNumbers" :key="n.type + n.number">
                  <span :class="{ 'ns-phone-mobile': n.type === 'mobile' }">{{ formatPhone(n.number) }}</span><span v-if="i < enrolledNumbers.length - 1">, </span>
                </template>
              </span>
            </div>
          </div>
          <!-- Status + 2FA share one wrapper so 2FA drops under Status (not onto its own row) when space runs out -->
          <div class="ns-sub-side">
          <div class="ns-sub-status">
            <div class="ns-sub-row">
              <span class="ns-sub-lbl">Status:</span>
              <!-- Click the status to change it (same actions as M01, saved to CallerInfo) -->
              <div class="ns-status-menu-wrap">
                <button class="ns-status-trigger" :style="{ color: authStatus.color }"
                  :disabled="!customerNumber || authSaving"
                  :title="customerNumber ? 'Change authentication status' : 'Look up a customer first'"
                  @click="statusMenuOpen = !statusMenuOpen">
                  {{ authSaving ? "Updating…" : authStatus.label }}
                  <i class="fa-solid fa-chevron-down ns-status-chevron"></i>
                </button>
                <div v-if="statusMenuOpen" class="ns-status-menu-backdrop" @click="statusMenuOpen = false"></div>
                <div v-if="statusMenuOpen" class="ns-status-menu">
                  <button class="ns-status-option ns-status-option--pass" :disabled="authStatusNum === '3'" @click="chooseAuth('3')">
                    <i class="fa-solid fa-circle-check"></i> Manually Authenticate
                  </button>
                  <button class="ns-status-option ns-status-option--fail" :disabled="authStatusNum === '4'" @click="chooseAuth('4')">
                    <i class="fa-solid fa-circle-xmark"></i> Manually Fail
                  </button>
                  <button v-if="authRevertTo !== null" class="ns-status-option" @click="chooseAuth('revert')">
                    <i class="fa-solid fa-rotate-left"></i> Undo last change
                  </button>
                </div>
              </div>
              <img v-if="isVerified" :src="logocheck" alt="authenticated" class="ns-status-icon" />
              <img v-else :src="logoclose" alt="not authenticated" class="ns-status-icon" />
            </div>
            <div class="ns-sub-row">
              <span class="ns-sub-lbl">Intent:</span>
              <span class="ns-sub-val" style="font-weight:600;">{{ currentIntent }}</span>
            </div>
            <div class="ns-sub-row" v-if="interactionSource">
              <span class="ns-sub-lbl">Source:</span>
              <span class="ns-sub-val">{{ interactionSource }}</span>
            </div>
            <div class="ns-sub-row">
              <span class="ns-sub-lbl">Service:</span>
              <span class="ns-sub-val">{{ internet.plan }}</span>
            </div>
          </div>

          <!-- 2FA — top right -->
          <div class="ns-subheader-2fa">
            <fieldset class="ns-fieldset" style="margin-top:0;">
              <legend class="ns-legend">2FA</legend>
              <div style="display:flex; align-items:center; gap:6px;">
                <select class="ns-input" v-model="otpPhone" style="margin:0; flex:1;" :disabled="!otpPhoneOptions.length">
                  <option v-if="!otpPhoneOptions.length" value="">No mobile number on file</option>
                  <option v-for="p in otpPhoneOptions" :key="p" :value="p">{{ formatPhone(p) }}</option>
                </select>
                <button class="ns-submit-btn" @click="sendSMS" :disabled="!otpPhone" style="margin:0; white-space:nowrap;">Send SMS</button>
              </div>
              <div style="display:flex; align-items:center; gap:6px;">
                <input class="ns-input" v-model="otpInput" placeholder="OTP" style="margin:0; width:80px; flex:none;" />
                <img v-if="verificationAttempted && isValid"  :src="logocheck" alt="valid"   class="ns-status-icon" />
                <img v-if="verificationAttempted && !isValid" :src="logoclose" alt="invalid" class="ns-status-icon" />
                <button class="ns-submit-btn" @click="verifyOTP" :disabled="!otpPhone || !otpInput" style="margin:0;">Validate</button>
              </div>
            </fieldset>
          </div>
          </div><!-- /ns-sub-side -->
        </div>


        <!-- ── 3-column body ───────────────────────────────────────────────── -->
        <div class="ns-centerdiv">

          <!-- ═══ COL 1: Identification + warnings ══════════════════════════ -->
          <div class="ns-col">
            <!-- Customer search (same lookups as M01) -->
            <h4>Search for</h4>
            <form @submit.prevent="searchCustomer">
              <input class="ns-input" v-model="searchInput" :disabled="searching" placeholder="Enter Customer Number" />
              <div style="margin: 8px 0 0;">
                <button type="submit" class="ns-submit-btn" :disabled="!searchInput.trim() || searching">
                  {{ searching ? "Searching…" : "Send" }}
                </button>
                <button type="button" class="ns-cancel-btn" @click="cancelSearch" :disabled="searching">Cancel</button>
              </div>
            </form>

            <!-- Fraud warnings (same checks as M01's Threshold Warning) -->
            <h4 style="margin-top:20px;">Fraud Warnings</h4>
            <div class="ns-warnings">
              <div v-if="callerIdFlags && callerIdFlags.failedAuthThisMember > 0" class="ns-warning">
                This caller ID has <b>{{ callerIdFlags.failedAuthThisMember }}</b>
                failed authentication attempt{{ callerIdFlags.failedAuthThisMember !== 1 ? "s" : "" }} on this account.
              </div>
              <div v-if="callerIdUsage.length" class="ns-usage">
                <div class="ns-usage-title">
                  Caller ID <span class="ns-usage-id">{{ formatPhone(callerId) }}</span> accessed these customer numbers:
                </div>
                <div v-for="(row, i) in callerIdUsage" :key="i" class="ns-usage-row"
                  :class="{ 'is-current': String(row.MemberNumber) === customerNumber }">
                  <b>{{ row.MemberNumber }}</b> — {{ row.callCount }} {{ row.callCount === 1 ? "time" : "times" }}
                </div>
              </div>
              <div v-for="(msg, i) in thresholdWarnings" :key="'t' + i" class="ns-warning">{{ msg }}</div>
              <div v-if="!hasFraudWarnings" class="ns-hint" style="margin:0; font-style:italic;">No warnings.</div>
            </div>

            <!-- Security notes (same as M01; saved with addFraudNotes) -->
            <h4 style="margin-top:20px; display:flex; align-items:center; gap:6px;">
              Security Notes
              <router-link v-if="fraudNotes.length" to="/notes" class="ns-count-badge" title="View all security notes">
                {{ fraudNotes.length }}
              </router-link>
            </h4>
            <form @submit.prevent="saveSecurityNote">
              <select class="ns-input" v-model="secCategory" style="margin-bottom:8px;">
                <option value="" disabled>Note Category</option>
                <option v-for="c in noteCategories" :key="c" :value="c">{{ c }}</option>
              </select>
              <textarea class="ns-textarea" v-model="secNote" rows="4" placeholder="Add a security note..."></textarea>
              <div style="margin-top:8px;">
                <button type="submit" class="ns-submit-btn" :disabled="!customerNumber || !secCategory || !secNote.trim() || secSaving">
                  {{ secSaving ? "Saving…" : "Save" }}
                </button>
                <button type="button" class="ns-cancel-btn" :disabled="(!secCategory && !secNote) || secSaving"
                  @click="secCategory = ''; secNote = ''">Cancel</button>
              </div>
            </form>

            <template v-if="intentPath.length">
              <h4 style="margin-top:20px;">Caller Journey</h4>
              <div class="ns-journey">
                <span v-for="(step, i) in intentPath" :key="i" class="ns-journey-step">
                  {{ typeof step === 'string' ? step : (step.intent || step.name || step.label) }}
                  <span v-if="i < intentPath.length - 1" class="ns-journey-arrow">›</span>
                </span>
              </div>
            </template>
          </div>

          <!-- ═══ COL 2: CRM ════════════════════════════════════════════════ -->
          <div class="ns-col">
            <div class="ns-col-head">
              <h4 style="margin:0;">Customer Interactions <span class="ns-source">Source: CRM</span></h4>
              <span class="ns-link" @click="openCrmActivity(null)">View all</span>
            </div>

            <select v-model="crmChannelFilter" class="ns-input" style="margin:10px 0; width:auto;">
              <option value="">All channels</option>
              <option value="voice">Voice</option>
              <option value="sms">SMS</option>
              <option value="chat">Chat / Email</option>
            </select>

            <div class="ns-timeline">
              <div v-if="crmLoading" class="ns-hint">Loading…</div>
              <div v-else-if="!crmFilteredRecords.length" class="ns-hint">No interactions found.</div>
              <div v-for="rec in crmFilteredRecords.slice(0, 5)" :key="rec.activityId"
                class="ns-timeline-item" @click="openCrmActivity(rec)">
                <i class="fa-solid ns-timeline-icon" :class="channelIcon(rec.channel)"></i>
                <div style="flex:1; min-width:0;">
                  <div class="ns-timeline-title">{{ rec.activitySubject }}</div>
                  <div class="ns-timeline-meta">
                    {{ rec.channel }}<span v-if="rec.wrapupCode"> · {{ rec.wrapupCode }}</span>
                  </div>
                </div>
                <span class="ns-timeline-time">{{ crmTimeAgo(rec.interactionDatetime) }}</span>
              </div>
            </div>

            <div v-if="crmSummary" class="ns-summary">
              <div class="ns-summary-title">Recent Interactions Summary</div>
              <div v-if="crmSummary.themes && crmSummary.themes.length" style="display:flex; flex-wrap:wrap; gap:4px; margin-bottom:8px;">
                <span class="ns-tag" v-for="(theme, i) in crmSummary.themes" :key="i">{{ theme }}</span>
              </div>
              <div style="white-space:pre-wrap; line-height:1.5;">{{ crmSummary.summary }}</div>
            </div>

            <h4 style="margin-top:20px;">Call Notes</h4>
            <textarea class="ns-textarea" v-model="crmNote" rows="4" placeholder="Add a note to the CRM for this call..."></textarea>
            <div style="margin-top:8px;">
              <button class="ns-submit-btn" @click="saveCrmNote" :disabled="!crmNote.trim() || crmNoteSaving">Save</button>
              <button class="ns-cancel-btn" @click="crmNote = ''" :disabled="!crmNote.trim()">Cancel</button>
            </div>
            <div v-if="sessionNotes.length" style="margin-top:10px;">
              <div v-for="(n, i) in sessionNotes" :key="i" class="ns-session-note">
                <span class="ns-session-note-time">{{ n.at }}</span> {{ n.note }}
              </div>
            </div>
          </div>

          <!-- ═══ COL 3: Outage + Internet Service + Billing ════════════════ -->
          <div class="ns-col">
            <h4>Outage Status</h4>
            <div class="ns-outage" :class="outage.active ? 'ns-outage--active' : 'ns-outage--clear'">
              <i class="fa-solid" :class="outage.active ? 'fa-triangle-exclamation' : 'fa-circle-check'"></i>
              <div style="flex:1; min-width:0;">
                <div style="font-weight:700;">
                  {{ outage.active ? `${outage.service} outage` : "No known outages" }} — {{ outage.area }}
                </div>
                <div style="font-size:13px; margin-top:2px;">{{ outage.message }}</div>
              </div>
            </div>
            <template v-if="outage.active">
              <table class="ns-info-table" style="margin-top:8px;">
                <tbody>
                  <tr><td class="ns-td-lbl">Cause</td><td>{{ outage.cause }}</td></tr>
                  <tr><td class="ns-td-lbl">Started</td><td>{{ outage.started }}</td></tr>
                  <tr><td class="ns-td-lbl">Est. Resolution</td><td style="font-weight:700;">{{ outage.etr }}</td></tr>
                  <tr><td class="ns-td-lbl">Affected</td><td>{{ outage.affected }}</td></tr>
                </tbody>
              </table>
            </template>

            <h4 style="margin-top:20px;">Internet Service</h4>
            <div class="ns-ids">
              <div class="ns-id-item">
                <span class="ns-sub-lbl">Plan</span>
                <span class="ns-id-val">{{ internet.plan }}</span>
              </div>
              <div class="ns-id-item">
                <span class="ns-sub-lbl">Status</span>
                <span><span :class="statusBadge(internet.status)">{{ internet.status }}</span></span>
              </div>
            </div>
            <table class="ns-info-table" style="margin-top:8px;">
              <tbody>
                <tr><td class="ns-td-lbl">Speed</td><td>{{ internet.speed }}</td></tr>
                <tr><td class="ns-td-lbl">Bundle</td><td>{{ internet.bundle }}</td></tr>
                <tr><td class="ns-td-lbl">Installed</td><td>{{ internet.installed }}</td></tr>
                <tr><td class="ns-td-lbl">Monthly</td><td>{{ fmt(internet.monthly) }}</td></tr>
              </tbody>
            </table>

            <h4 style="margin-top:20px;">Billing</h4>
            <table class="ns-info-table">
              <tbody>
                <tr><td class="ns-td-lbl">Balance Due</td><td style="font-weight:600;">{{ fmt(billing.balance) }}</td></tr>
                <tr><td class="ns-td-lbl">Due Date</td><td>{{ billing.due }}</td></tr>
                <tr><td class="ns-td-lbl">AutoPay</td><td>{{ billing.autopay ? "Enrolled" : "Not enrolled" }}</td></tr>
                <tr><td class="ns-td-lbl">Last Payment</td><td>{{ billing.lastPayment }}</td></tr>
              </tbody>
            </table>
          </div>

        </div>
      </template>
    </div><!-- /ns-card -->

    <!-- ── Start Callback phone modal (caller not authenticated) ─────────── -->
    <div class="ns-modal-backdrop" v-if="callbackModal.show" @click.self="callbackModal.show = false">
      <div class="ns-modal" style="width:400px;">
        <div class="ns-modal-header">
          <div style="font-size:16px; font-weight:700;">Start Callback</div>
          <button class="ns-modal-close" @click="callbackModal.show = false">✕</button>
        </div>
        <div class="ns-modal-body">
          <label class="ns-detail-lbl" for="ns-callback-phone">PHONE NUMBER</label>
          <input id="ns-callback-phone" class="ns-input" type="tel" v-model="callbackModal.phone"
            placeholder="e.g. +1 317 326 3131" @keydown.enter="submitCallbackModal" />
        </div>
        <div class="ns-modal-footer">
          <button class="ns-cancel-btn" @click="callbackModal.show = false">Cancel</button>
          <button class="ns-submit-btn" :disabled="!callbackModal.phone.trim() || isCallbackLoading" @click="submitCallbackModal">
            {{ isCallbackLoading ? "Creating…" : "Start Callback" }}
          </button>
        </div>
      </div>
    </div>

    <!-- ── CRM Activity Modal ────────────────────────────────────────────── -->
    <div class="ns-modal-backdrop" v-if="crmModalOpen" @click.self="closeCrmActivity">
      <div class="ns-modal">
        <div class="ns-modal-header">
          <div>
            <div style="font-size:12px; opacity:0.8;">
              {{ crmSelected ? moment(crmSelected.interactionDatetime).format('MMM D, YYYY · h:mm A') : 'CRM' }}
            </div>
            <div style="font-size:16px; font-weight:700;">{{ crmSelected ? crmSelected.activitySubject : 'Customer Interactions' }}</div>
          </div>
          <button class="ns-modal-close" @click="closeCrmActivity">✕</button>
        </div>

        <!-- List -->
        <div v-if="!crmSelected" class="ns-modal-body">
          <select v-model="crmModalChannelFilter" class="ns-input" style="width:auto; margin-bottom:10px;">
            <option value="">All channels</option>
            <option value="voice">Voice</option>
            <option value="sms">SMS</option>
            <option value="chat">Chat / Email</option>
          </select>
          <div v-if="!crmModalFilteredRecords.length" class="ns-hint" style="text-align:center; padding:24px;">No interactions found.</div>
          <div v-for="rec in crmModalFilteredRecords" :key="rec.activityId" class="ns-list-item" @click="crmSelected = rec">
            <i class="fa-solid ns-timeline-icon" :class="channelIcon(rec.channel)"></i>
            <div style="flex:1; min-width:0;">
              <div class="ns-timeline-title">{{ rec.activitySubject }}</div>
              <div class="ns-timeline-meta">
                {{ moment(rec.interactionDatetime).format('MMM D, YYYY · h:mm A') }} · {{ rec.channel }}
                <span :class="rec.direction === 'Inbound' ? 'ns-badge-in' : 'ns-badge-out'">{{ (rec.direction || 'Outbound').toUpperCase() }}</span>
                <strong v-if="rec.wrapupCode"> · {{ rec.wrapupCode }}</strong>
              </div>
              <div v-if="rec.intents && rec.intents.length" style="margin-top:4px;">
                <span class="ns-tag" v-for="(intent, i) in rec.intents" :key="i">{{ intent }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Detail -->
        <div v-else class="ns-modal-body">
          <button class="ns-back-btn" @click="crmSelected = null">← Back</button>
          <div class="ns-detail-grid">
            <div><div class="ns-detail-lbl">DATE &amp; TIME</div><div>{{ moment(crmSelected.interactionDatetime).format('MMM D, YYYY · h:mm A') }}</div></div>
            <div><div class="ns-detail-lbl">QUEUE</div><div>{{ crmSelected.queueName || '—' }}</div></div>
            <div><div class="ns-detail-lbl">AGENT</div><div>{{ crmSelected.agentName || '—' }}</div></div>
            <div v-if="(crmSelected.channel || '').toLowerCase().includes('chat')">
              <div class="ns-detail-lbl">SOURCE IP</div>
              <div><img :src="US_FLAG_URL" alt="US" class="ns-flag" />{{ crmSelected.sourceIp || CHAT_SOURCE_IP }}</div>
            </div>
            <div v-else>
              <div class="ns-detail-lbl">PHONE</div>
              <div>
                <img v-if="crmSelected.callerId" :src="US_FLAG_URL" alt="US" class="ns-flag" />{{ crmSelected.callerId ? formatPhone(crmSelected.callerId) : '—' }}
              </div>
            </div>
            <div>
              <div class="ns-detail-lbl">DIRECTION / CHANNEL</div>
              <div>{{ crmSelected.channel }} <span :class="crmSelected.direction === 'Inbound' ? 'ns-badge-in' : 'ns-badge-out'">{{ (crmSelected.direction || 'Outbound').toUpperCase() }}</span></div>
            </div>
            <div>
              <div class="ns-detail-lbl">DURATION</div>
              <div>{{ crmSelected.durationSec ? Math.floor(crmSelected.durationSec / 60) + ':' + String(crmSelected.durationSec % 60).padStart(2, '0') : '—' }}</div>
            </div>
          </div>
          <div class="ns-detail-section" v-if="crmSelected.wrapupCode">
            <div class="ns-detail-lbl">WRAP-UP / DISPOSITION</div>
            <span class="ns-tag">{{ crmSelected.wrapupCode }}</span>
          </div>
          <div class="ns-detail-section" v-if="crmSelected.intents && crmSelected.intents.length">
            <div class="ns-detail-lbl">INTENT</div>
            <span class="ns-tag" v-for="(intent, i) in crmSelected.intents" :key="i">{{ intent }}</span>
          </div>
          <div class="ns-detail-section" v-if="crmSelected.aiSummary">
            <div class="ns-detail-lbl">AI SUMMARY</div>
            <div class="ns-detail-box" style="white-space:pre-wrap;">{{ crmSelected.aiSummary }}</div>
          </div>
          <div class="ns-detail-section">
            <div class="ns-detail-lbl">NOTES</div>
            <div class="ns-detail-box" style="white-space:pre-wrap;">{{ crmSelectedNotes }}</div>
          </div>
        </div>

        <div class="ns-modal-footer">
          <button class="ns-cancel-btn" @click="closeCrmActivity">Close</button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.ns-page {
  font-family: "Open Sans", "Segoe UI", sans-serif;
  font-size: 15px;
  background: #e8e8e8;
  min-height: 100vh;
  padding: 20px;
  color: #222;
  box-sizing: border-box;
}
.ns-card {
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 1px 6px rgba(0,0,0,0.12);
  overflow: hidden;
  container-type: inline-size;
}
.ns-loading { padding: 40px; text-align: center; color: #888; }

/* ── Header ─────────────────────────────────────────────────────────────────── */
.ns-topbar {
  padding: 14px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #e8e8e8;
}
.ns-screen-id   { font-size: 18px; font-weight: 600; color: #222; margin: 0; }
.ns-brand       { font-size: 13px; font-weight: 600; color: #0e5071; margin-left: 10px; }
.ns-screen-code { font-size: 18px; font-weight: 600; color: #888; }
.ns-topbar-right { display: flex; align-items: center; gap: 14px; }
.ns-goto-btn {
  background: #0e5071;
  color: #fff;
  border: none;
  border-radius: 50px;
  padding: 6px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.ns-goto-btn:hover { background: #0c4160; }

/* ── Subheader ───────────────────────────────────────────────────────────────── */
/* Full width: details | status ........ 2FA (original look).
   Medium: 2FA wraps under status, beside the details.  Small: everything stacks.
   Plain flex-wrap, no container queries, so it works in any embedded browser. */
.ns-subheader {
  border-bottom: 1px solid #e8e8e8;
  padding: 10px 20px;
  display: flex;
  flex-wrap: wrap;
  gap: 12px 60px;
  align-items: flex-start;
}
.ns-sub-main { flex: 0 1 auto; min-width: 0; max-width: 100%; }
.ns-sub-side {
  flex: 1 1 280px;
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px 40px;
}
.ns-sub-status { flex: 0 1 auto; min-width: 0; }
.ns-subheader-2fa { flex: 0 1 320px; width: 320px; max-width: 100%; }
.ns-sub-row { display: flex; align-items: center; gap: 6px; padding: 1px 0; font-size: 14px; }
.ns-sub-lbl { color: #555; min-width: 110px; flex-shrink: 0; }
.ns-sub-val { color: #222; min-width: 0; overflow-wrap: anywhere; }
.ns-sub-val--name { font-weight: 600; }
.ns-sub-val--number { font-family: monospace; font-weight: 700; color: #0e5071; }
.ns-copy-btn { background: none; border: none; cursor: pointer; color: #6b7280; padding: 0 2px; font-size: 13px; }
.ns-copy-btn:hover { color: #0e5071; }
.ns-sub-row--top { align-items: flex-start; }

/* ── Fraud warnings / security notes ─────────────────────────────────────────── */
.ns-warnings {
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 10px;
  font-size: 13px;
  max-height: 260px;
  overflow-y: auto;
}
.ns-warning {
  padding: 6px 8px;
  margin-bottom: 6px;
  background: #fff5f5;
  border-left: 3px solid #e74c3c;
  border-radius: 0 3px 3px 0;
  color: #c0392b;
  line-height: 1.4;
}
.ns-usage { margin-bottom: 8px; }
.ns-usage-title { font-weight: 600; color: #333; margin-bottom: 4px; }
.ns-usage-id { color: #0e5071; }
.ns-usage-row { padding: 3px 8px; border-radius: 4px; margin-bottom: 2px; background: #f5f5f5; color: #555; }
.ns-usage-row.is-current { background: #e8f4fd; }
.ns-count-badge {
  display: inline-block;
  min-width: 20px;
  padding: 0 6px;
  border-radius: 10px;
  background: #0e5071; /* same teal as M01's notes badge */
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  line-height: 18px;
  text-align: center;
  text-decoration: none;
}
.ns-callback-btn {
  margin-left: 6px;
  background: #0e5071;
  color: #fff;
  border: none;
  border-radius: 20px;
  padding: 2px 10px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}
.ns-callback-btn:hover { background: #0c4160; }
.ns-callback-btn:disabled { opacity: 0.55; cursor: default; }
.ns-callback-status { font-size: 11px; white-space: nowrap; }
.ns-callback-status.is-ok  { color: #16a34a; }
.ns-callback-status.is-err { color: #dc2626; }
.ns-flag { width: 20px; height: 15px; margin-right: 4px; vertical-align: middle; flex-shrink: 0; }
.ns-phone-mobile { color: #48b02c; } /* same green M01 uses for mobile numbers */
.ns-status-icon { width: 16px; height: 16px; flex-shrink: 0; }

/* ── 3-column layout ─────────────────────────────────────────────────────────── */
.ns-centerdiv { display: flex; align-items: stretch; }
.ns-col {
  flex: 1;
  padding: 16px 20px;
  min-width: 0;
  border-right: 1px solid #e8e8e8;
}
.ns-col:last-child { border-right: none; }
.ns-col h4 { font-size: 14px; font-weight: 600; margin: 0 0 10px; color: #222; }
.ns-col-head { display: flex; justify-content: space-between; align-items: center; }
.ns-source { font-size: 12px; font-weight: 400; color: #888; margin-left: 6px; }
.ns-link { cursor: pointer; color: #0e5071; font-size: 13px; }

@container (max-width: 1100px) {
  .ns-centerdiv { flex-wrap: wrap; }
  .ns-col { flex: 1 1 50%; border-bottom: 1px solid #e8e8e8; }
  .ns-col:nth-child(2) { border-right: none; }
  .ns-col:nth-child(3) { flex-basis: 100%; border-bottom: none; }
}
@container (max-width: 720px) {
  .ns-col { flex-basis: 100% !important; border-right: none; padding: 14px; }
  .ns-topbar { padding: 12px 14px; }
}
@media (max-width: 900px) { .ns-page { padding: 10px; } }
@media (max-width: 600px) { .ns-page { padding: 0; } .ns-card { border-radius: 0; } }

/* ── ID strip ────────────────────────────────────────────────────────────────── */
.ns-ids {
  display: flex;
  background: #f5f7f9;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
}
.ns-id-item {
  flex: 1;
  padding: 7px 10px;
  border-right: 1px solid #e0e0e0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.ns-id-item:last-child { border-right: none; }
.ns-id-val { font-size: 13px; font-weight: 600; color: #0e5071; }

/* ── Inputs / buttons ────────────────────────────────────────────────────────── */
.ns-input, .ns-textarea {
  width: 100%;
  border: 1px solid #ccc;
  border-radius: 3px;
  padding: 5px 8px;
  font-size: 14px;
  font-family: inherit;
  box-sizing: border-box;
  background: #fff;
}
.ns-textarea { resize: vertical; padding: 6px 8px; }
.ns-input:focus, .ns-textarea:focus { outline: none; border-color: #0e5071; }

.ns-fieldset {
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 8px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.ns-legend { font-weight: 600; font-size: 13px; color: #374151; padding: 0 6px; }

.ns-submit-btn, .ns-cancel-btn, .ns-back-btn {
  border: none;
  border-radius: 20px;
  padding: 5px 18px;
  font-size: 13px;
  cursor: pointer;
  margin-right: 6px;
}
.ns-submit-btn { background: #0e5071; color: #fff; }
.ns-submit-btn:hover { background: #0c4160; }
.ns-cancel-btn { background: #b0b0b0; color: #fff; }
.ns-cancel-btn:hover { background: #999; }
.ns-submit-btn:disabled, .ns-cancel-btn:disabled { opacity: 0.55; cursor: default; }
.ns-back-btn { background: #f3f4f6; color: #374151; margin-bottom: 12px; }

/* ── Tables ──────────────────────────────────────────────────────────────────── */
.ns-info-table { width: 100%; border-collapse: collapse; font-size: 14px; }
.ns-info-table td { padding: 4px 6px; vertical-align: top; }
.ns-td-lbl { color: #555; width: 130px; white-space: nowrap; }

.ns-data-table { width: 100%; border-collapse: collapse; font-size: 13px; table-layout: fixed; }
.ns-data-table thead tr { background: #0e5071; }
.ns-data-table th { padding: 7px 10px; text-align: left; font-weight: 600; color: #fff !important; white-space: nowrap; }
.ns-data-table tbody tr:nth-child(even) { background: #f5f7f9; }
.ns-data-table tbody tr { border-bottom: 1px solid #e8e8e8; }
.ns-data-table td { padding: 6px 10px; vertical-align: top; color: #333; overflow-wrap: anywhere; }

/* ── CRM timeline ────────────────────────────────────────────────────────────── */
.ns-timeline { border: 1px solid #e5e7eb; border-radius: 6px; overflow: hidden; }
.ns-timeline-item, .ns-list-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 9px 12px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
}
.ns-timeline-item:last-child { border-bottom: none; }
.ns-timeline-item:hover, .ns-list-item:hover { background: #f5f8fb; }
.ns-timeline-icon { color: #0e5071; width: 16px; text-align: center; margin-top: 3px; }
.ns-timeline-title { font-size: 13px; font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ns-timeline-meta { font-size: 12px; color: #6b7280; }
.ns-timeline-time {font-size: 11px; color: #9ca3af; white-space: nowrap; }
.ns-timeline .ns-hint { padding: 12px; margin: 0; }

.ns-summary {
  margin-top: 16px;
  padding: 12px 14px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 13px;
  color: #374151;
}
.ns-summary-title { font-weight: 600; margin-bottom: 8px; }
.ns-tag {
  display: inline-block;
  background: #e0ecf3;
  color: #0e5071;
  border-radius: 10px;
  padding: 1px 8px;
  font-size: 11px;
  font-weight: 600;
  margin-right: 4px;
}
.ns-session-note {
  font-size: 13px;
  padding: 6px 10px;
  margin-bottom: 6px;
  background: #f8fafc;
  border-left: 3px solid #0e5071;
  border-radius: 0 4px 4px 0;
}
.ns-session-note-time { color: #888; font-size: 11px; margin-right: 6px; }

.ns-journey { display: flex; flex-wrap: wrap; gap: 4px; font-size: 12px; }
.ns-journey-step { background: #f3f4f6; border-radius: 10px; padding: 2px 8px; color: #374151; }
.ns-journey-arrow { color: #9ca3af; margin-left: 4px; }

/* ── Ticket / outage ─────────────────────────────────────────────────────────── */
.ns-callout {
  border: 1px solid #bfdbfe;
  border-left: 4px solid #0e5071;
  background: #f0f7ff;
  border-radius: 4px;
  padding: 10px 12px;
}
.ns-callout-head { display: flex; justify-content: space-between; align-items: center; gap: 8px; margin-bottom: 6px; }
.ns-callout-num { font-family: monospace; font-weight: 700; font-size: 14px; color: #0e5071; }
.ns-callout-title { font-weight: 600; font-size: 14px; }
.ns-callout-meta { font-size: 12px; color: #555; margin-top: 2px; }
.ns-callout-detail { font-size: 13px; color: #374151; margin-top: 6px; }


/* ── Manual authentication ───────────────────────────────────────────────────── */
.ns-status-menu-wrap { position: relative; display: inline-block; }
.ns-status-trigger {
  background: none;
  border: 1px solid transparent;
  border-radius: 4px;
  padding: 0 4px;
  margin-left: -4px;
  font: inherit;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 5px;
}
.ns-status-trigger:hover:not(:disabled) { border-color: #d1d5db; background: #f9fafb; }
.ns-status-trigger:disabled { cursor: default; }
.ns-status-trigger:disabled .ns-status-chevron { display: none; }
.ns-status-chevron { font-size: 10px; color: #6b7280; }
.ns-status-menu-backdrop { position: fixed; inset: 0; z-index: 20; }
.ns-status-menu {
  position: absolute;
  top: calc(100% + 4px);
  left: -4px;
  z-index: 21;
  min-width: 210px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  box-shadow: 0 6px 20px rgba(0,0,0,0.14);
  padding: 4px 0;
}
.ns-status-option {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  background: none;
  border: none;
  padding: 8px 12px;
  font: inherit;
  font-size: 13px;
  text-align: left;
  cursor: pointer;
  color: #374151;
}
.ns-status-option:hover:not(:disabled) { background: #f3f4f6; }
.ns-status-option:disabled { opacity: 0.45; cursor: default; }
.ns-status-option--pass i { color: #15803d; }
.ns-status-option--fail i { color: #b91c1c; }

.ns-outage { display: flex; align-items: center; gap: 10px; padding: 10px 12px; border-radius: 6px; font-size: 13px; }
.ns-outage i { font-size: 18px; }
.ns-outage--clear  { background: #ecfdf5; color: #065f46; border: 1px solid #a7f3d0; }
.ns-outage--active { background: #fef2f2; color: #991b1b; border: 1px solid #fecaca; }

/* ── Badges ──────────────────────────────────────────────────────────────────── */
.ns-badge-active, .ns-badge-pending, .ns-badge-rejected, .ns-badge-muted {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
}
.ns-badge-active   { background: #d1fae5; color: #065f46; }
.ns-badge-pending  { background: #fef3c7; color: #92400e; }
.ns-badge-rejected { background: #fee2e2; color: #991b1b; }
.ns-badge-muted    { background: #f3f4f6; color: #6b7280; }
.ns-badge-in, .ns-badge-out {
  display: inline-block;
  font-size: 10px;
  font-weight: 700;
  border-radius: 4px;
  padding: 0 5px;
  margin-left: 4px;
}
.ns-badge-in  { background: #dbeafe; color: #1e40af; }
.ns-badge-out { background: #f3e8ff; color: #6b21a8; }

.ns-hint { font-size: 12px; color: #888; margin: 0 0 8px; }

/* ── Modal ───────────────────────────────────────────────────────────────────── */
.ns-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.4);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}
.ns-modal {
  background: #fff;
  border-radius: 8px;
  width: 640px;
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - 48px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0,0,0,0.24);
}
.ns-modal-header {
  background: #0e5071;
  color: #fff;
  padding: 14px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.ns-modal-close { background: none; border: none; color: #fff; font-size: 18px; cursor: pointer; opacity: 0.85; }
.ns-modal-body { padding: 16px 20px; overflow-y: auto; flex: 1; }
.ns-modal-footer { padding: 10px 20px; border-top: 1px solid #e8e8e8; text-align: right; }
.ns-detail-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px 20px; font-size: 14px; margin-bottom: 14px; }
.ns-detail-lbl { font-size: 11px; font-weight: 700; color: #6b7280; letter-spacing: 0.04em; margin-bottom: 3px; }
.ns-detail-section { margin-bottom: 14px; }
.ns-detail-box { background: #f8fafc; border: 1px solid #e5e7eb; border-radius: 4px; padding: 8px 10px; font-size: 14px; }
</style>
